// src/context/AuthContext.jsx
import { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react'
import { supabase } from '../supabase'

const AuthContext = createContext({})

// Idle-logout timing. Deliberately generous (accountants can spend long
// stretches reading a TB/AFS without touching the mouse/keyboard) — this is
// about an unattended shared/office machine staying logged in indefinitely,
// not about interrupting active review. A visible warning fires 2 minutes
// before the actual logout so a genuinely-present user gets a chance to
// dismiss it, rather than being logged out with no notice.
// IMPORTANT: this does NOT put work at risk. Step-boundary progress
// (Phase 5, Issue A — see kailux-fixes-log.md) is already autosaved to
// Supabase at each stage, independent of the browser session, so an idle
// logout mid-flow loses nothing beyond requiring a re-login.
const IDLE_LOGOUT_MS  = 30 * 60 * 1000   // 30 minutes of no activity
const IDLE_WARNING_MS = 2  * 60 * 1000   // warn 2 minutes before logout
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart']

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null)
  const [firm,    setFirm]    = useState(null)
  const [loading, setLoading] = useState(true)
  const [idleWarning, setIdleWarning] = useState(false)
  const warningTimerRef = useRef(null)
  const logoutTimerRef  = useRef(null)
  // Which user's firm we have already loaded (or are loading). Used to tell a
  // genuine sign-in apart from supabase-js re-announcing the SAME session
  // (TOKEN_REFRESHED, SIGNED_IN on tab refocus, INITIAL_SESSION).
  const loadedUserIdRef = useRef(null)

  useEffect(() => {
    // Check current session on load
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser((prev) => (prev?.id === session?.user?.id ? prev : (session?.user ?? null)))
      if (!session?.user) setLoading(false)
      // INITIAL_SESSION from the listener below may already be loading it.
      else if (loadedUserIdRef.current !== session.user.id) loadFirm(session.user.id, session.access_token)
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!session?.user) {
          loadedUserIdRef.current = null
          setUser(null)
          setFirm(null)
          setLoading(false)
          return
        }

        // Oct 2026 request-storm fix (see kailux-fixes-log.md): supabase-js
        // fires this callback for EVERY auth event on a live session, not
        // only real sign-ins -- TOKEN_REFRESHED, SIGNED_IN again whenever
        // the tab regains focus, INITIAL_SESSION on mount. This handler used
        // to treat each one as a brand-new login: setLoading(true) made
        // ProtectedRoute swap the entire app for its spinner (unmounting the
        // current page), then loadFirm() re-fetched /api/firms/me and
        // setLoading(false) remounted the page, which re-fired all of ITS
        // own fetches. Any source of repeated auth events therefore became an
        // endless remount + refetch loop (a live new signup produced ~1
        // full cycle per second until requests started failing with 401).
        // Only a change of user is a real transition; the same user's
        // session being re-announced changes nothing the UI depends on.
        if (loadedUserIdRef.current === session.user.id) {
          setUser((prev) => (prev?.id === session.user.id ? prev : session.user))
          return
        }

        setUser(session.user)
        // Bug (found 15 Sep 2026 building a demo account): `loading` was
        // only ever flipped true->false once, right after the very first
        // getSession() check on page load. A *later* sign-in (the normal
        // login-form flow, or SSO/magic-link) re-enters this branch with
        // `loading` already false and never resets it before kicking off
        // loadFirm's async fetch. Login.jsx navigates to /dashboard the
        // instant signInWithPassword resolves, without waiting for this
        // listener to even run -- so ProtectedRoute could render at any
        // point in a real race: before this listener fires at all (user
        // still null -> wrongly redirected to /login), after it sets user
        // but before loadFirm resolves (firm still null -> wrongly
        // redirected to /signup-incomplete), or after both finish
        // (correct). All three were reproduced live, intermittently, on
        // the same working credentials. Setting loading back to true here
        // makes ProtectedRoute show its spinner through the whole
        // transition instead of racing to a wrong conclusion.
        setLoading(true)
        // Hand the token over directly: calling supabase.auth.getSession()
        // from inside this callback re-enters the auth lock supabase-js is
        // holding while it runs listeners, and can itself trigger another
        // refresh (and so another event).
        loadFirm(session.user.id, session.access_token)
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  const loadFirm = async (userId, accessToken) => {
    loadedUserIdRef.current = userId
    try {
      let token = accessToken
      if (!token) {
        const {
          data: { session },
        } = await supabase.auth.getSession()
        token = session?.access_token
      }
      if (!token) { loadedUserIdRef.current = null; setFirm(null); return }
      const res = await fetch(
        `${import.meta.env.VITE_API_URL || 'https://kailux-platform-production.up.railway.app'}/api/firms/me`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      if (!res.ok) {
        // Previously swallowed with zero visibility. This endpoint is also
        // where firms.last_login_at used to get touched (now handled by a
        // DB trigger on auth.users instead -- see migration 064 -- so a
        // failure here no longer causes a "Never signed in" false negative
        // in FirmManagement), but a failed /api/firms/me still means the
        // user is stuck with firm === null, so at least log it.
        console.error(`/api/firms/me returned ${res.status}`)
        // Let the next auth event retry rather than being de-duplicated away.
        loadedUserIdRef.current = null
        setFirm(null)
        return
      }
      const data = await res.json()
      setFirm(data)
    } catch (err) {
      loadedUserIdRef.current = null
      console.error('Could not load firm:', err)
    } finally {
      setLoading(false)
    }
  }

  // Re-read the firm record in place (e.g. after a team member is removed and the billed seats
  // change). Unlike loadFirm, a failure leaves the current firm untouched instead of nulling it,
  // so a flaky refresh can never knock the user back to a "no firm" state.
  const refreshFirm = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      if (!token) return
      const res = await fetch(
        `${import.meta.env.VITE_API_URL || 'https://kailux-platform-production.up.railway.app'}/api/firms/me`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      if (res.ok) setFirm(await res.json())
    } catch (err) {
      console.error('Could not refresh firm:', err)
    }
  }, [])

  const login = async (email, password) => {
    // Set before the call, not after -- Login.jsx navigates to /dashboard
    // the instant this function resolves, with no guarantee
    // onAuthStateChange (which normally flips this back to true, see
    // above) has run yet. Setting it here closes that first race window
    // outright instead of hoping the listener wins the race.
    setLoading(true)
    loadedUserIdRef.current = null   // a fresh login must always load the firm
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) setLoading(false)
    return { data, error }
  }

  const logout = useCallback(async (reason) => {
    await supabase.auth.signOut()
    loadedUserIdRef.current = null
    setUser(null)
    setFirm(null)
    setIdleWarning(false)
    if (reason === 'idle') {
      // Flag consumed by Login.jsx to show "You were signed out after a
      // period of inactivity" instead of a silent redirect to the login
      // screen, which otherwise looks indistinguishable from a bug.
      try { sessionStorage.setItem('kailux_idle_logout', '1') } catch { /* ignore */ }
    }
  }, [])

  const clearIdleTimers = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current)
    if (logoutTimerRef.current)  clearTimeout(logoutTimerRef.current)
  }, [])

  const resetIdleTimer = useCallback(() => {
    clearIdleTimers()
    setIdleWarning(false)
    warningTimerRef.current = setTimeout(() => setIdleWarning(true), IDLE_LOGOUT_MS - IDLE_WARNING_MS)
    logoutTimerRef.current  = setTimeout(() => logout('idle'), IDLE_LOGOUT_MS)
  }, [clearIdleTimers, logout])

  // Idle-activity tracking only runs while a user is actually signed in.
  useEffect(() => {
    if (!user) { clearIdleTimers(); setIdleWarning(false); return }

    resetIdleTimer()
    const onActivity = () => resetIdleTimer()
    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, onActivity, { passive: true }))

    return () => {
      ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, onActivity))
      clearIdleTimers()
    }
  }, [user, resetIdleTimer, clearIdleTimers])

  // "Stay signed in" — dismisses the warning and restarts the countdown,
  // used by the warning banner/modal rendered wherever the app shell lives.
  const stayLoggedIn = useCallback(() => resetIdleTimer(), [resetIdleTimer])

  return (
    <AuthContext.Provider value={{
      user, firm, loading, login, logout, loadFirm, refreshFirm,
      idleWarning, stayLoggedIn,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
